import { RepeatIcon } from "@chakra-ui/icons";
import { Text, VStack } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useGetChat from "../../hooks/api/chat/useGetChat";
import usePostMessageOnChat from "../../hooks/api/chat/usePostMessageOnChat";
import { ChatMessage, ChatParticipant, ChatType } from "../../models/Chat";
import { Homework } from "../../models/Homework";
import useAuthState from "../../state/auth.state";
import useChatState from "../../state/chat.state";
import ErrorSection from "../ErrorSection";
import ChatMessageItem from "../items/ChatMessageItem";
import LoadingSection from "../LoadingSection";

type Props = {
  chatId: string;
  chatType: ChatType;
  isInteractive: boolean;
  homeworkStatus?: Homework["status"];
};

export default function ChatContainer({
  chatId,
  chatType,
  isInteractive,
  homeworkStatus,
}: Props) {
  const { t } = useTranslation();
  const {
    data: chat,
    isLoading: isChatLoading,
    refetch: refetchChat,
    error: chatError,
  } = useGetChat(chatId, chatType);
  const { user, role } = useAuthState();
  const { mutateAsync: postMessageOnChat } = usePostMessageOnChat(
    chatId,
    chatType,
  );
  const {
    messageWithTeacher,
    messageWithStudents,
    isChatWithTeacherDisabled,
    isChatWithStudentsDisabled,
    setMessageWithTeacher,
    setMessageWithStudents,
    setIsChatWithTeacherDisabled,
    setIsChatWithStudentsDisabled,
  } = useChatState();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });

    if (chatType === "chat-with-teacher") {
      if (role === "ROLE_STUDENT" && messages.length === 0) {
        setIsChatWithTeacherDisabled(true);
        return;
      }

      setIsChatWithTeacherDisabled(false);
    } else if (chatType === "chat-with-students") {
      if (role === "ROLE_STUDENT" && homeworkStatus !== "APPROVED") {
        setIsChatWithStudentsDisabled(true);
        return;
      }

      setIsChatWithStudentsDisabled(false);
    }

    return () => {
      if (chatType === "chat-with-teacher") {
        setIsChatWithTeacherDisabled(true);
      } else {
        setIsChatWithStudentsDisabled(true);
      }
    };
  }, [
    homeworkStatus,
    messages,
    role,
    setIsChatWithStudentsDisabled,
    setIsChatWithTeacherDisabled,
    chatType,
  ]);

  useEffect(() => {
    if (!isInteractive) return;

    if (messageWithTeacher) {
      postMessageOnChat({ message: messageWithTeacher }).then((message) =>
        setMessages((oldMessages) => [...oldMessages, message]),
      );
      setMessageWithTeacher("");
    } else if (messageWithStudents) {
      postMessageOnChat({ message: messageWithStudents }).then((message) =>
        setMessages((oldMessages) => [...oldMessages, message]),
      );
      setMessageWithStudents("");
    }
  }, [
    isInteractive,
    messageWithStudents,
    messageWithTeacher,
    postMessageOnChat,
    setMessageWithStudents,
    setMessageWithTeacher,
    setMessages,
  ]);

  useEffect(() => {
    setMessages(chat?.messages ?? []);
  }, [chat?.messages]);

  const sendersMapping = useMemo(
    () => ({
      ...{
        [user!.id]: {
          id: user!.id,
          fullName: user!.fullName,
          email: user!.email,
          sender: true,
        },
      },
      ...chat?.users.reduce<{ [key: number]: ChatParticipant }>(
        (p, c) => ({ ...p, [c.id]: c }),
        {},
      ),
    }),
    [chat?.users, user],
  );

  if (isChatLoading) return <LoadingSection />;

  if (chatError && chatError.request.status !== 404) {
    return <ErrorSection retry={refetchChat} />;
  }

  return (
    <VStack position="relative" flexGrow={1} gap={2}>
      {(chatType === "chat-with-teacher" && isChatWithTeacherDisabled) ||
      (chatType === "chat-with-students" && isChatWithStudentsDisabled) ? (
        <Text mt={12}>{t("homework_page.chat_disabled")}</Text>
      ) : chatError?.request.status === 404 && !messages.length ? (
        <Text mt={12}>{t("homework_page.no_messages_yet")}</Text>
      ) : (
        <>
          {messages.map((message) => (
            <ChatMessageItem
              key={message.id}
              sender={sendersMapping[message.userId]}
              message={message}
              isFromLoggedUser={message?.userId === user?.id}
            />
          ))}
          <RepeatIcon
            cursor="pointer"
            w={5}
            h={5}
            color="primary.DEFAULT"
            mt={12}
            alignSelf="center"
            onClick={() => refetchChat()}
          />
        </>
      )}
      <div ref={bottomRef} />
    </VStack>
  );
}
